import KosmoButton from "@/UI/Buttons/KosmoButton"
import SettingSection from "@/modules/shared/SettingSection"
import { Divider, Dropdown, Menu, Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import React, { useEffect } from "react"
import DriverModal from "./components/DriverModal"
import { useDrivers } from "@/hooks/useDrivers"
import Image from "next/image"
import { vehicleImage } from "@/utils/providers"
import { formatPhoneNumber } from "@/utils/FormatPhoneNumber"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCheck,
  faChevronDown,
  faDownload,
  faEdit,
  faEllipsis,
  faFile,
  faPlus,
  faTrashCan,
  faUpload,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { DropdownItemContainer } from "../OrganizationSettings/components/OrgUsersActionsMenu"
import ConfirmModal from "@/UI/Modals/ConfirmModal"
import { isEmpty, orderBy } from "lodash"
import { Loader } from "@/UI"
import { useRecoilState, useRecoilValue } from "recoil"
import { isPayingUserSelector, userAtom } from "@/atoms/userAtom"
import { useTeams } from "@/hooks/useTeams"
import { message } from "antd"
import { exportDrivers } from "@/api/drivers"
import DriverUpload from "./components/DriverUploadModal"
import { getCapacityText } from "./utils"
import { useFlags } from "flagsmith/react"
import { driversAtom } from "@/atoms/driversAtom"
import DriverAttachmentsModal from "./components/DriverAttachmentsModal"

const fileDownload = require("js-file-download")

interface DriversSettingsProps {}

const TitleHeader: React.FC<{ children: React.ReactNode; centered?: boolean }> = ({
  children,
  centered,
}) => (
  <p className={`text-[#6B7280] uppercase font-medium ${!!centered && "text-center"}`}>
    {children}
  </p>
)

const DriversSettings: React.FC<DriversSettingsProps> = () => {
  const { t } = useTranslation()
  const flags = useFlags(["nash_edg_demo"])
  const { org } = useRecoilValue(userAtom)
  const isPayingUser = useRecoilValue(isPayingUserSelector)
  const [uploadOpen, setUploadOpen] = React.useState<boolean>(false)
  const { removeDriver, isLoading, fetchDrivers } = useDrivers()
  const [drivers, setDrivers] = useRecoilState(driversAtom)
  const { teams, fetchTeams } = useTeams()

  useEffect(() => {
    fetchTeams(true)
    fetchDrivers(true)
  }, [org?.id])

  const handleExport = async () => {
    try {
      const { data } = await exportDrivers()
      fileDownload(data, "drivers.csv")
    } catch (error: any) {
      message.error(error.message)
      return error
    }
  }

  const handleDriverIdCopy = (driverId: string) => {
    navigator.clipboard?.writeText(driverId)
    message.success(t("common.copied"))
  }

  const menu = (
    <Menu>
      <Menu.Item key="2" onClick={() => setUploadOpen(true)} disabled={!isPayingUser}>
        <FontAwesomeIcon className="mr-2" icon={faUpload} />
        {t("profile.DriversSettings.importDrivers")}
      </Menu.Item>
    </Menu>
  )

  return (
    <SettingSection
      title={t("common.drivers")}
      actionButton={
        <div className="flex w-full justify-end gap-4">
          <DriverUpload
            open={uploadOpen}
            onClose={() => {
              setUploadOpen(false)
            }}
          />
          <DriverModal
            button={
              <Dropdown.Button
                overlay={menu}
                type="primary"
                trigger={["click"]}
                style={{
                  width: "auto",
                }}
                icon={<FontAwesomeIcon icon={faChevronDown} color="#FFF" />}
              >
                <div
                  className="flex items-center gap-x-2"
                  style={{
                    fontSize: "14px",
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} color="#FFF" />
                  <p>{t("common.addDriver")}</p>
                </div>
              </Dropdown.Button>
            }
          />
          <KosmoButton type="default" onClick={() => handleExport()}>
            {t("profile.DriversSettings.exportDrivers")}
            <FontAwesomeIcon className="ml-2" icon={faDownload} />
          </KosmoButton>
        </div>
      }
    >
      <div className="flex flex-col max-w-[100vw] overflow-x-auto w-full">
        <div
          className={`flex flex-col bg-white rounded-lg mt-5 mb-5 shadow-kosmo min-w-[700px] overflow-x-scroll`}
        >
          <div className="grid grid-cols-12 w-full px-6 py-4">
            <div className="col-span-2">
              <TitleHeader>{t("common.fullName")}</TitleHeader>
            </div>
            <div className="col-span-2">
              <TitleHeader>ID</TitleHeader>
            </div>
            <div className="col-span-2">
              <TitleHeader>{t("common.email")}</TitleHeader>
            </div>
            <div className="col-span-2">
              <TitleHeader centered>{t("common.phoneNumber")}</TitleHeader>
            </div>
            <div className="col-span-1">
              <TitleHeader centered>{t("common.teams")}</TitleHeader>
            </div>
            <div className="col-span-1">
              <TitleHeader centered>{t("common.vehicle")}</TitleHeader>
            </div>
            <div className="col-span-1">
              <TitleHeader centered>{t("common.capacity")}</TitleHeader>
            </div>
            {flags.nash_edg_demo.enabled && (
              <div className="col-span-1">
                <div className="w-1/2">
                  <TitleHeader centered>{t("common.active")}</TitleHeader>
                </div>
              </div>
            )}
          </div>
          <Divider style={{ margin: 0 }} />
          {isLoading ? (
            <div className="flex w-full justify-center py-10 ">
              <Loader fontSize={46} />
            </div>
          ) : (
            <div className="grid grid-cols-12 w-full items-center px-6 py-4  text-[#374151]">
              {isEmpty(drivers) ? (
                <div className="col-span-12 text-center">
                  <p className="font-bold">{t("profile.DriversSettings.emptyFleet")}</p>
                </div>
              ) : (
                orderBy(drivers, ["disabled", "name", "id"], ["desc", "asc", "desc"])?.map(
                  (driver) => {
                    const driverTeams = isEmpty(driver?.teamIDs)
                      ? ""
                      : driver?.teamIDs
                          ?.map((teamId) => teams?.find((team) => team?.id === teamId)?.name)
                          ?.filter((team) => team)
                          ?.join(", ")
                    return (
                      <React.Fragment key={driver?.id}>
                        <div className="col-span-2 capitalize font-medium">{driver?.name}</div>
                        <div className="col-span-2 text-start text-ellipsis whitespace-nowrap overflow-hidden text-sm">
                          <span
                            onClick={() => handleDriverIdCopy(driver.id)}
                            className="text-sm text-gray-500 underline decoration-dotted cursor-pointer hover:bg-gray-50 p-1.5 rounded-md"
                          >
                            {driver?.id}
                          </span>
                        </div>
                        <div
                          title={driver?.email}
                          className="col-span-2 text-ellipsis whitespace-nowrap overflow-hidden"
                        >
                          {driver?.email || "-"}
                        </div>
                        <div className="col-span-2 text-center text-sm">
                          {formatPhoneNumber(driver.phone)}
                        </div>
                        <div
                          title={driverTeams}
                          className="col-span-1 capitalize text-center font-medium text-ellipsis whitespace-nowrap overflow-hidden"
                        >
                          <Tooltip title={driverTeams}>{driverTeams}</Tooltip>
                        </div>
                        <div className="col-span-1 text-center">
                          <Image
                            src={vehicleImage(driver.vehicleType)}
                            priority={true}
                            width={40}
                            height={40}
                          />
                        </div>
                        <div className="col-span-1 text-center">
                          {getCapacityText({
                            length: driver.lengthCapacity,
                            width: driver.widthCapacity,
                            height: driver.heightCapacity,
                            weight: driver.weightCapacity,
                          })}
                        </div>
                        {/* ENG DEMO */}
                        <div
                          className={`col-span-1 flex text-center items-center ${
                            flags.nash_edg_demo.enabled ? "justify-between" : "justify-end"
                          }`}
                        >
                          {flags.nash_edg_demo.enabled && (
                            <div className="w-1/2">
                              {driver.disabled ? (
                                <FontAwesomeIcon
                                  className="flex-shrink-0 h-4 text-gray-500"
                                  icon={faXmark}
                                />
                              ) : (
                                <FontAwesomeIcon
                                  className="flex-shrink-0 h-4 text-primary"
                                  icon={faCheck}
                                />
                              )}
                            </div>
                          )}
                          {/*END ENG DEMO */}
                          <Dropdown
                            overlay={
                              <Menu>
                                <Menu.Item key={0}>
                                  <DriverModal
                                    button={
                                      <div>
                                        <DropdownItemContainer
                                          text={t("common.edit")}
                                          icon={faEdit}
                                        />
                                      </div>
                                    }
                                    driver={driver}
                                  />
                                </Menu.Item>
                                <Menu.Item key={1}>
                                  <ConfirmModal
                                    button={
                                      <div>
                                        <DropdownItemContainer
                                          text={t("common.remove")}
                                          icon={faTrashCan}
                                        />
                                      </div>
                                    }
                                    content={{
                                      type: "danger",
                                      title: t("profile.DriversSettings.removeDriver"),
                                      description: t(
                                        "profile.DriversSettings.deleteDriverConfirmation"
                                      ),
                                      cancelText: t("common.cancel"),
                                      okText: t("common.yesDelete"),
                                    }}
                                    onConfirm={() => removeDriver(driver.id)}
                                  />
                                </Menu.Item>
                                {flags.nash_edg_demo.enabled && (
                                  <Menu.Item key={2}>
                                    <div
                                      onClick={() =>
                                        setDrivers((driversState) => {
                                          const driverToUpdate = driversState.find(
                                            (driverFromState) => driverFromState.id === driver.id
                                          )
                                          if (driverToUpdate) {
                                            return [
                                              ...driversState?.filter(
                                                (driverFromState) =>
                                                  driverFromState.id !== driver.id
                                              ),
                                              { ...driverToUpdate, disabled: !driver.disabled },
                                            ]
                                          }
                                          return driversState
                                        })
                                      }
                                    >
                                      <DropdownItemContainer
                                        text={
                                          driver.disabled
                                            ? t("common.activate")
                                            : t("common.deactivate")
                                        }
                                        icon={driver.disabled ? faCheck : faXmark}
                                      />
                                    </div>
                                  </Menu.Item>
                                )}
                                {/* ENG DEMO ATTACHMENTS */}
                                {flags.nash_edg_demo.enabled && !isEmpty(driver?.attachments) && (
                                  <Menu.Item key={3}>
                                    <DriverAttachmentsModal
                                      button={
                                        <div>
                                          <DropdownItemContainer
                                            text={t("Documents")}
                                            icon={faFile}
                                          />
                                        </div>
                                      }
                                      driver={driver}
                                    />
                                  </Menu.Item>
                                )}
                                {/* ENG DEMO ATTACHMENTS */}
                              </Menu>
                            }
                            trigger={["click"]}
                          >
                            <div className={"flex justify-end cursor-pointer"}>
                              <FontAwesomeIcon
                                className="flex-shrink-0 h-4 text-gray-500 text-sm cursor-pointer"
                                icon={faEllipsis}
                              />
                            </div>
                          </Dropdown>
                        </div>
                      </React.Fragment>
                    )
                  }
                )
              )}
            </div>
          )}
        </div>
      </div>
    </SettingSection>
  )
}

export default DriversSettings
